import { useEffect, useState } from 'react';
import {
  getBrowserTimeZone,
  HOUR_HEIGHT as DESKTOP_HOUR_HEIGHT,
  formatMinutesLabel,
  layoutDayEvents,
  getDayAvailability,
  snapMinutes,
  toDayKey,
} from '../utils';
import { JoinMeetButton } from './MeetLinkBlock';

const HOURS = Array.from({ length: 24 }, (_, i) => i);
const COMPACT_LAPTOP_QUERY =
  '(min-width: 1024px) and (max-width: 1440px) and (max-height: 899px)';
const COMPACT_HOUR_HEIGHT = 48;

function useHourHeight() {
  const [hourHeight, setHourHeight] = useState(() => {
    if (typeof window === 'undefined') return DESKTOP_HOUR_HEIGHT;
    return window.matchMedia(COMPACT_LAPTOP_QUERY).matches
      ? COMPACT_HOUR_HEIGHT
      : DESKTOP_HOUR_HEIGHT;
  });

  useEffect(() => {
    const media = window.matchMedia(COMPACT_LAPTOP_QUERY);
    const apply = () => {
      setHourHeight(media.matches ? COMPACT_HOUR_HEIGHT : DESKTOP_HOUR_HEIGHT);
    };
    apply();
    media.addEventListener('change', apply);
    return () => media.removeEventListener('change', apply);
  }, []);

  return hourHeight;
}

function nowMinutesLocal(now = new Date()) {
  const parts = new Intl.DateTimeFormat('en-GB', {
    timeZone: getBrowserTimeZone(),
    hour: '2-digit',
    minute: '2-digit',
    hour12: false,
  }).formatToParts(now);
  const h = Number(parts.find((p) => p.type === 'hour')?.value || 0);
  const m = Number(parts.find((p) => p.type === 'minute')?.value || 0);
  return h * 60 + m;
}

function shortTime(minutes, compact = false) {
  const label = formatMinutesLabel(minutes).replace(':00', '');
  if (!compact) return label;
  return label.replace(' ', '').replace('AM', 'a').replace('PM', 'p');
}

function DayColumn({
  day,
  events,
  settings,
  canCreate,
  onSlotCreate,
  onEventClick,
  nowMinutes,
  showNowLine,
  hourHeight,
}) {
  const laidOut = layoutDayEvents(events);
  const availability = getDayAvailability(day.key, settings);
  const closed = !availability.available;

  const handleGridClick = (e) => {
    e.stopPropagation();
    if (!canCreate) return;
    const rect = e.currentTarget.getBoundingClientRect();
    const y = e.clientY - rect.top;
    const minutes = snapMinutes((y / hourHeight) * 60);
    onSlotCreate?.(day.key, minutes);
  };

  return (
    <div
      className={`min-w-0 flex flex-col border-r border-[#EBF4EA] last:border-r-0 relative ${
        closed ? 'bg-[#FDF2F2]' : 'bg-white'
      }`}
    >
      <div
        className={`relative flex-1 ${canCreate ? 'cursor-pointer' : ''}`}
        style={{ height: hourHeight * 24 }}
        onClick={handleGridClick}
        role="presentation"
      >
        {HOURS.map((hour) => (
          <div
            key={hour}
            className={`absolute left-0 right-0 border-t ${closed ? 'border-[#F34343]/10' : 'border-[#F0F0F0]'}`}
            style={{ top: hour * hourHeight, height: hourHeight }}
          />
        ))}

        {showNowLine && nowMinutes != null && (
          <div
            className="absolute left-0 right-0 z-[2] pointer-events-none"
            style={{ top: (nowMinutes / 60) * hourHeight }}
          >
            <div className="relative">
              <span className="absolute -left-1.5 -top-1.5 w-3 h-3 rounded-full bg-red-500" />
              <div className="h-px bg-red-500 w-full" />
            </div>
          </div>
        )}

        {laidOut.map((event) => {
          const styles = {
            scheduled: 'bg-[#FEF5E7] border-l-[#F1A132] text-[#1F2241]',
            completed: 'bg-[#EBF4EA] border-l-[#50AB3D] text-[#1F2241]',
            cancelled: 'bg-[#FDECEC] border-l-[#F34343] text-[#1F2241]',
          };
          const eventStyle = styles[event.status] || styles.scheduled;

          const top = (event.startMinutes / 60) * hourHeight;
          const height = Math.max(
            22,
            ((event.endMinutes - event.startMinutes) / 60) * hourHeight - 2,
          );
          const widthPct = 100 / event.laneCount;
          const leftPct = event.lane * widthPct;

          return (
            <div
              key={event.id}
              role="button"
              tabIndex={0}
              onClick={(e) => {
                e.stopPropagation();
                onEventClick?.(event, e.currentTarget.getBoundingClientRect());
              }}
              onKeyDown={(e) => {
                if (e.key === 'Enter' || e.key === ' ') {
                  e.preventDefault();
                  onEventClick?.(
                    event,
                    e.currentTarget.getBoundingClientRect(),
                  );
                }
              }}
              className={`absolute z-[2] overflow-hidden rounded-md border-l-[3px] border-y-0 border-r-0 px-1.5 sm:px-2 py-0.5 text-left text-[9px] sm:text-[11px] font-medium shadow-sm hover:brightness-95 flex items-start gap-1 cursor-pointer ${eventStyle}`}
              style={{
                top,
                height,
                left: `calc(${leftPct}% + 2px)`,
                width: `calc(${widthPct}% - 4px)`,
              }}
              title={`${event.title} · ${formatMinutesLabel(event.startMinutes)} – ${formatMinutesLabel(event.endMinutes)}`}
            >
              <span className="truncate leading-tight block">
                {event.title}
              </span>
              <span className="shrink-0 text-opacity-80 leading-tight">
                {shortTime(event.startMinutes)}
              </span>
              {event.status !== 'cancelled' && event.googleMeetLink ? (
                <JoinMeetButton href={event.googleMeetLink} />
              ) : null}
            </div>
          );
        })}
      </div>
    </div>
  );
}

export function TimeGridView({
  days = [],
  eventsByDay = {},
  settings,
  canCreate,
  onSlotCreate,
  onEventClick,
  scrollRef,
}) {
  const [nowMinutes, setNowMinutes] = useState(() => nowMinutesLocal());
  const todayKey = toDayKey(new Date());

  useEffect(() => {
    const id = setInterval(() => setNowMinutes(nowMinutesLocal()), 60_000);
    return () => clearInterval(id);
  }, []);

  const hourHeight = useHourHeight();
  const isDayView = days.length === 1;
  const minGridWidth = isDayView ? undefined : days.length <= 3 ? 360 : 640;

  return (
    <div className="time-grid flex flex-col flex-1 min-h-[460px] sm:min-h-[500px] lg:min-h-0 overflow-hidden border border-[#EBF4EA] rounded-xl m-1 sm:m-2 lg:m-4 bg-white">
      <div
        ref={scrollRef}
        className="overflow-auto custom-scrollbar flex-1 min-h-0"
      >
        <div
          className="grid"
          style={{
            minWidth: minGridWidth,
            gridTemplateColumns: `${isDayView ? 48 : 56}px repeat(${days.length}, minmax(${isDayView ? 0 : 72}px, 1fr))`,
          }}
        >
          <div className="sticky top-0 left-0 z-20 bg-white border-b border-r border-[#EBF4EA]" />
          {days.map((day) => {
            const availability = getDayAvailability(day.key, settings);
            const closed = !availability.available;
            const isToday = day.isToday;

            let headerBg = 'bg-white';
            let headerTextName = 'text-[#868686]';
            let headerTextNum = 'text-[#1F2241]';

            if (isToday) {
              headerBg = 'bg-[#50AB3D] text-white';
              headerTextName = 'text-white/90';
              headerTextNum = 'text-white';
            } else if (closed) {
              headerBg = 'bg-[#FDF2F2]';
              headerTextName = 'text-[#F34343]';
              headerTextNum = 'text-[#F34343]';
            }

            return (
              <div
                key={`hdr-${day.key}`}
                className={`sticky top-0 z-10 px-2 py-2 border-b border-r border-[#EBF4EA] last:border-r-0 text-center flex flex-col items-center justify-center gap-0.5 ${headerBg}`}
              >
                <div
                  className={`text-[10px] sm:text-[11px] font-semibold uppercase ${headerTextName}`}
                >
                  {(day.label || day.weekdayKey || '').slice(0, 3)}
                </div>
                <div
                  className={`text-sm sm:text-base font-semibold ${headerTextNum}`}
                >
                  {day.day}
                </div>
                {!availability.available && (
                  <div
                    className={`text-[10px] sm:text-[11px] font-medium mt-1 ${headerTextNum}`}
                  >
                    {availability.type === 'full_day_off'
                      ? availability.label || 'Holiday'
                      : 'Closed'}
                  </div>
                )}
                {availability.available && availability.type === 'half_day' && (
                  <div
                    className={`text-[10px] sm:text-[11px] font-medium mt-1 text-[#B224EF]`}
                  >
                    {availability.label &&
                    !['Half day', 'Half Day'].includes(availability.label)
                      ? availability.label
                      : 'Half Day'}
                  </div>
                )}
              </div>
            );
          })}

          <div className="sticky left-0 z-10 bg-white border-r border-[#EBF4EA]">
            <div style={{ height: hourHeight * 24 }} className="relative">
              {HOURS.map((hour) => (
                <div
                  key={hour}
                  className="absolute left-0 right-0 pr-2 text-right text-[10px] sm:text-[11px] font-medium text-[#868686] -translate-y-1/2"
                  style={{ top: hour * hourHeight }}
                >
                  {hour === 0
                    ? ''
                    : formatMinutesLabel(hour * 60)
                        .replace(':00', '')
                        .replace(' ', '')}
                </div>
              ))}
            </div>
          </div>

          {days.map((day) => (
            <DayColumn
              key={day.key}
              day={day}
              events={eventsByDay[day.key] || []}
              settings={settings}
              canCreate={canCreate}
              onSlotCreate={onSlotCreate}
              onEventClick={onEventClick}
              nowMinutes={nowMinutes}
              showNowLine={day.key === todayKey}
              hourHeight={hourHeight}
            />
          ))}
        </div>
      </div>
    </div>
  );
}
